/**
 * Product add-ons: Fabric (variant picker), Metalramme / Topmadras / Madras (auto-match toggle).
 * Selected add-ons are added as child line items with the main product on ATC.
 */
(() => {
  if (window.__productAddonsBooted) return;
  window.__productAddonsBooted = true;

  const AS_PICTURED_DEFAULT = 'som på billedet';
  const DESIGN_YOURSELF = 'design selv';

  /** Persist selection across Prestige product:rerender */
  const selectionStore = new Map();

  function parseJson(script) {
    if (!script) return [];
    try {
      return JSON.parse(script.textContent.trim());
    } catch (error) {
      console.warn('[ProductAddons] Failed to parse variants JSON', error);
      return [];
    }
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  /**
   * Return a stable key for dimensions written with different separators.
   * Shopify product variants use values such as "120X200", while the
   * mattress products use values such as "120/200".
   */
  function getDimensionKey(value) {
    const match = normalize(value).match(/(?:^|\D)(\d{2,3})\s*(?:x|×|\/|\*)\s*(\d{2,3})(?:\D|$)/);
    if (!match) return '';
    return `${Number(match[1])}x${Number(match[2])}`;
  }

  function getVariantDimensionKey(variant) {
    return [variant?.option1, variant?.option2, variant?.option3, variant?.title, variant?.sku]
      .map(getDimensionKey)
      .find(Boolean) || '';
  }

  function formatMoney(cents) {
    const format = window.themeVariables?.settings?.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables?.settings?.moneyFormat;
    const template = format || '{{amount_with_comma_separator}} kr';
    const amount = Number(cents) / 100;
    const formats = {
      amount: amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      amount_no_decimals: Math.round(amount)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      amount_with_comma_separator: amount
        .toFixed(2)
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      amount_no_decimals_with_comma_separator: Math.round(amount)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      amount_with_apostrophe_separator: amount
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    };

    return template.replace(
      /\{\{\s*(\w+)\s*\}\}/,
      (_, placeholder) => formats[placeholder] ?? formats.amount
    );
  }

  function getMainForm(formId) {
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId) return byId;
    }
    return document.querySelector('form[action*="/cart/add"]');
  }

  function getVariantPicker(form) {
    if (form?.id) {
      const byForm = document.querySelector(`variant-picker[form-id="${form.id}"]`);
      if (byForm) return byForm;
    }
    return (
      form?.closest('product-rerender, .product')?.querySelector('variant-picker') ||
      document.querySelector('variant-picker')
    );
  }

  function getMainVariantData(form) {
    const picker = getVariantPicker(form);
    const script = picker?.querySelector('script[data-variant]');
    if (script) {
      try {
        return JSON.parse(script.textContent.trim());
      } catch {
        /* fall through */
      }
    }

    const idInput = form?.querySelector('[name="id"]:not([disabled]), [name="id"]');
    return idInput
      ? {
          id: Number(idInput.value),
          title: '',
          sku: '',
          option1: null,
          option2: null,
          option3: null
        }
      : null;
  }

  function getMainProductHandle(form) {
    const picker = getVariantPicker(form);
    return normalize(picker?.getAttribute('handle') || '');
  }

  function getOptionLabel(input) {
    if (!input) return '';
    const byFor = input.id
      ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`)
      : null;
    const labelEl = input.closest('label') || byFor || input.nextElementSibling;
    return labelEl?.textContent || input.getAttribute('aria-label') || '';
  }

  /**
   * Match addon variant to main product variant.
   * Priority: dimensions → SKU → title → options → product handle → first available.
   */
  function matchAddonVariant(variants, mainVariant, mainHandle) {
    if (!Array.isArray(variants) || variants.length === 0) return null;

    const available = variants.filter((v) => v.available !== false);
    const pool = available.length ? available : variants;

    if (!mainVariant && !mainHandle) return pool[0];

    // Match the actual bed dimensions before comparing the full variant title.
    // This deliberately treats 120X200, 120x200, 120×200 and 120/200 as equal.
    const mainDimension = getVariantDimensionKey(mainVariant);
    if (mainDimension) {
      const byDimension = available.find(
        (variant) => getVariantDimensionKey(variant) === mainDimension
      );
      if (byDimension) return byDimension;

      // Never substitute the first available size when the selected bed has
      // explicit dimensions but that add-on size is unavailable.
      return null;
    }

    if (pool.length === 1) return pool[0];

    const mainSku = normalize(mainVariant?.sku);
    if (mainSku) {
      const bySku = pool.find((v) => normalize(v.sku) === mainSku);
      if (bySku) return bySku;
    }

    const mainTitle = normalize(mainVariant?.title);
    if (mainTitle && mainTitle !== 'default title') {
      const byTitle = pool.find((v) => normalize(v.title) === mainTitle);
      if (byTitle) return byTitle;
    }

    const mainOptions = [mainVariant?.option1, mainVariant?.option2, mainVariant?.option3]
      .map(normalize)
      .filter(Boolean);

    if (mainOptions.length) {
      const byOptions = pool.find((v) => {
        const addonOptions = [v.option1, v.option2, v.option3].map(normalize).filter(Boolean);
        if (!addonOptions.length) return false;
        return (
          mainOptions.every((opt) => addonOptions.includes(opt)) ||
          addonOptions.every((opt) => mainOptions.includes(opt))
        );
      });
      if (byOptions) return byOptions;
    }

    if (mainHandle) {
      const byHandle = pool.find((v) => {
        const haystack = [v.title, v.sku, v.barcode, v.option1, v.option2, v.option3]
          .map(normalize)
          .join(' ');
        return haystack.includes(mainHandle) || normalize(v.title) === mainHandle;
      });
      if (byHandle) return byHandle;
    }

    return pool[0];
  }

  class ProductAddon {
    constructor(root, controller) {
      this.root = root;
      this.controller = controller;
      this.id = root.dataset.addonId || root.dataset.addonProductId || '';
      this.mode = root.dataset.addonMode || 'auto_match';
      this.type = root.dataset.addonType || '';
      this.productTitle = root.dataset.addonProductTitle || '';
      this.formId = root.dataset.formId;
      this.asPicturedValue = normalize(root.dataset.asPicturedValue || AS_PICTURED_DEFAULT);
      this.variants = parseJson(root.querySelector('script[data-addon-variants]'));
      this.trigger = root.querySelector('[data-addon-trigger]');
      this.variantInput = root.querySelector('[data-addon-variant-id]');
      this.panel = root.querySelector('[data-addon-variants-panel]');
      this.selected = false;
      this.selectedVariantId = null;
      this.selectedFabricReference = null;
      this.selectedFabricLabel = null;

      this.restoreFromStore();
    }

    restoreFromStore() {
      const saved = selectionStore.get(this.id);
      if (!saved?.selected) return;

      this.selected = true;
      this.selectedVariantId = saved.variantId || null;
      this.selectedFabricReference = saved.fabricReference || null;
      this.selectedFabricLabel = saved.fabricLabel || null;

      if (this.mode === 'fabric') {
        if (this.selectedVariantId) {
          const radio = this.root.querySelector(
            `[data-addon-variant-option][value="${this.selectedVariantId}"]`
          );
          if (radio) radio.checked = true;
        }
        this.restoreFabricChoice();
        this.openPanel();
      } else {
        // Auto-matched add-ons must always be recalculated. A saved variant ID
        // belongs to the previously selected bed size and may now be stale.
        this.selectAutoMatch({ silent: true });
        return;
      }

      this.syncUi();
    }

    saveToStore() {
      if (!this.id) return;
      if (this.selected) {
        selectionStore.set(this.id, {
          selected: true,
          variantId: this.selectedVariantId,
          fabricReference: this.selectedFabricReference,
          fabricLabel: this.selectedFabricLabel
        });
      } else {
        selectionStore.delete(this.id);
      }
    }

    toggle() {
      if (this.mode === 'fabric') {
        if (this.controller.asPicturedSelected) {
          this.controller.clearAsPictured();
        } else if (this.controller.isMainVariantAsPicturedSelected()) {
          this.controller.selectDesignYourself();
        }
      }

      if (this.mode === 'fabric') {
        if (this.selected) {
          this.deselect();
        } else {
          this.selectFabric();
        }
        return;
      }

      if (this.selected) {
        this.deselect();
      } else {
        this.selectAutoMatch();
      }
    }

    selectFabric() {
      this.selected = true;

      // Prefer already checked radio, otherwise first available variant
      const checked = this.root.querySelector('[data-addon-variant-option]:checked');
      const firstAvailable =
        this.root.querySelector('[data-addon-variant-option]:not([disabled])') ||
        this.root.querySelector('[data-addon-variant-option]');

      const radio = checked || firstAvailable;
      if (radio) {
        radio.checked = true;
        this.selectedVariantId = Number(radio.value);
      } else if (this.variants.length) {
        const matched = matchAddonVariant(this.variants, null, '');
        this.selectedVariantId = matched?.id || null;
      }

      this.selectDefaultFabricChoice();

      this.openPanel();
      this.syncUi();
      this.saveToStore();
      this.controller.onAddonChange();
    }

    selectAutoMatch({ silent = false } = {}) {
      const form = getMainForm(this.formId);
      const mainVariant = getMainVariantData(form);
      const mainHandle = getMainProductHandle(form);
      const matched = matchAddonVariant(this.variants, mainVariant, mainHandle);

      this.syncMatchedPreview(matched);

      if (!matched?.id) {
        console.warn('[ProductAddons] No variant found for', this.type, this.variants);
        this.selected = false;
        this.selectedVariantId = null;
        this.syncUi();
        this.saveToStore();
        return;
      }

      this.selected = true;
      this.selectedVariantId = matched.id;
      this.syncUi();
      this.saveToStore();
      if (!silent) this.controller.onAddonChange();
    }

    syncMatchedPreview(variant) {
      const price = this.trigger?.querySelector('.product-addon__trigger-price');
      this.root.dataset.matchedVariantId = variant?.id ? String(variant.id) : '';

      if (this.trigger) {
        this.trigger.disabled = !variant?.id;
        this.trigger.setAttribute(
          'aria-label',
          variant?.id
            ? `${this.productTitle || this.type}: ${variant.title || ''}`
            : `${this.productTitle || this.type}: unavailable for selected size`
        );
      }

      if (!price) return;

      if (!variant?.id) {
        price.textContent = '';
        price.hidden = true;
        return;
      }

      const variantPrice = Number(variant.price || 0);
      price.hidden = false;
      price.classList.toggle('product-addon__trigger-price--free', variantPrice === 0);
      price.textContent = variantPrice > 0 ? `+${formatMoney(variantPrice)}` : 'Free';
    }

    selectFabricVariant(variantId) {
      this.selected = true;
      this.selectedVariantId = Number(variantId);
      this.selectedFabricReference = null;
      this.selectedFabricLabel = null;
      this.selectDefaultFabricChoice();
      this.syncUi();
      this.saveToStore();
      this.controller.onAddonChange();
    }

    getActiveFabricList() {
      if (!this.selectedVariantId) return null;
      return this.root.querySelector(
        `[data-addon-fabric-choices="${CSS.escape(String(this.selectedVariantId))}"]`
      );
    }

    selectDefaultFabricChoice() {
      const firstChoice = this.getActiveFabricList()?.querySelector('[data-addon-fabric-choice]');
      if (firstChoice) this.selectFabricChoice(firstChoice, { silent: true });
    }

    restoreFabricChoice() {
      const activeList = this.getActiveFabricList();
      if (!activeList) return;

      const savedChoice = this.selectedFabricReference
        ? Array.from(activeList.querySelectorAll('[data-addon-fabric-choice]')).find(
            (input) => input.dataset.fabricReference === String(this.selectedFabricReference)
          )
        : null;
      this.selectFabricChoice(savedChoice || activeList.querySelector('[data-addon-fabric-choice]'), {
        silent: true
      });
    }

    selectFabricChoice(input, { silent = false } = {}) {
      if (!input) return;
      input.checked = true;
      this.selectedFabricReference = input.dataset.fabricReference || input.value || null;
      this.selectedFabricLabel = input.dataset.fabricLabel || input.value || null;
      this.syncFabricChoices();
      this.saveToStore();
      if (!silent) this.controller.onAddonChange();
    }

    openPanel() {
      if (this.panel) this.panel.hidden = false;
    }

    closePanel() {
      if (this.panel) this.panel.hidden = true;
    }

    // Smoothly reveal the colour choices for the chosen fabric type.
    // Sticky-header offset, and never focuses a hidden control (no jump).
    scrollToChoices() {
      const target = this.getActiveFabricList() || this.panel;
      if (!target) return;
      const run = () => {
        if (target.hidden) return;
        const offset = 110; // clearance for the sticky header
        const top = window.pageYOffset + target.getBoundingClientRect().top - offset;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      };
      requestAnimationFrame(run);
      window.setTimeout(run, 300);
    }

    rematch(mainVariant = null) {
      if (this.mode === 'fabric') return;

      const form = getMainForm(this.formId);
      const currentMainVariant = mainVariant || getMainVariantData(form);
      const mainHandle = getMainProductHandle(form);
      const matched = matchAddonVariant(this.variants, currentMainVariant, mainHandle);

      this.syncMatchedPreview(matched);

      if (!this.selected) return;

      if (!matched?.id) {
        this.selected = false;
        this.selectedVariantId = null;
      } else {
        this.selectedVariantId = matched.id;
      }

      this.syncUi();
      this.saveToStore();
    }

    deselect({ silent = false } = {}) {
      this.selected = false;
      this.selectedVariantId = null;
      this.selectedFabricReference = null;
      this.selectedFabricLabel = null;
      this.root.querySelectorAll('[data-addon-variant-option]').forEach((input) => {
        input.checked = false;
      });
      this.closePanel();
      this.syncUi();
      this.saveToStore();
      if (!silent) this.controller.onAddonChange();
    }

    syncUi() {
      this.trigger?.classList.toggle('is-selected', this.selected);
      this.trigger?.setAttribute('aria-pressed', this.selected ? 'true' : 'false');
      this.root.classList.toggle('is-selected', this.selected);
      this.syncFabricChoices();

      if (this.variantInput) {
        if (this.selected && this.selectedVariantId) {
          this.variantInput.value = String(this.selectedVariantId);
          this.variantInput.removeAttribute('disabled');
        } else {
          this.variantInput.value = '';
          this.variantInput.setAttribute('disabled', '');
        }
      }
    }

    getSelectedVariant() {
      return this.variants.find(
        (variant) => Number(variant.id) === Number(this.selectedVariantId)
      );
    }

    getFabricDisplayLabel() {
      const family = String(this.getSelectedVariant()?.title || '').split('·')[0].trim();
      const color = String(this.selectedFabricLabel || '').trim();
      if (!color) return family;
      if (!family || normalize(color).startsWith(normalize(family))) return color;
      return `${family} ${color}`;
    }

    syncFabricChoices() {
      this.root.querySelectorAll('[data-addon-fabric-choices]').forEach((list) => {
        list.hidden = !(
          this.selected && String(this.selectedVariantId) === list.dataset.addonFabricChoices
        );
      });
    }

    getCartItem() {
      if (!this.selected || !this.selectedVariantId) return null;
      const selectedVariant = this.variants.find(
        (variant) => Number(variant.id) === Number(this.selectedVariantId)
      );
      const properties = {
        _addon_type: this.type,
        _addon_label:
          this.trigger?.querySelector('.product-addon__label')?.textContent?.trim() || this.type,
        _addon_product_title: this.productTitle,
        _addon_variant_title: selectedVariant?.title || '',
        _addon_price: String(selectedVariant?.price || 0)
      };
      if (this.selectedFabricLabel) properties['Valgt stof'] = this.selectedFabricLabel;
      if (this.selectedFabricReference) {
        properties._fabric_reference = this.selectedFabricReference;
      }

      return {
        id: Number(this.selectedVariantId),
        quantity: 1,
        properties
      };
    }
  }

  class ProductAddonsController {
    constructor(scope = document) {
      this.scope = scope;
      this.addons = [];
      this.bound = false;
      this.refreshing = false;
      this.asPicturedSelected = false;
      this.modeInitialized = false;
    }

    init() {
      const roots = Array.from(this.scope.querySelectorAll('[data-product-addon]'));
      this.addons = roots.map((root) => new ProductAddon(root, this));
      if (!this.modeInitialized) {
        const mainVariantIsAsPictured = this.isMainVariantAsPicturedSelected();
        this.asPicturedSelected = true;
        this.modeInitialized = true;
        if (!mainVariantIsAsPictured) {
          requestAnimationFrame(() => {
            if (!this.isMainVariantAsPicturedSelected()) this.selectAsPictured();
          });
        }
      }
      this.syncAsPicturedUi();
      this.addons.forEach((addon) => addon.rematch());
      this.updateModeSummary();
      this.updateDisplayedPrice();
      this.bindGlobalListeners();
    }

    bindGlobalListeners() {
      if (this.bound) return;
      this.bound = true;

      // Event delegation — survives product:rerender DOM swaps
      document.addEventListener('click', (event) => {
        const designTrigger = event.target.closest?.('[data-addon-design-yourself]');
        if (designTrigger) {
          event.preventDefault();
          event.stopPropagation();
          this.selectDesignMode();
          return;
        }

        const asPicturedTrigger = event.target.closest?.('[data-addon-as-pictured]');
        if (asPicturedTrigger) {
          event.preventDefault();
          event.stopPropagation();
          this.selectAsPictured();
          return;
        }

        const trigger = event.target.closest?.('[data-addon-trigger]');
        if (!trigger) return;

        const root = trigger.closest('[data-product-addon]');
        if (!root) return;

        event.preventDefault();
        event.stopPropagation();

        const addon = this.addons.find((item) => item.root === root);
        if (addon) addon.toggle();
      });

      document.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;

        // Fabric variant radios
        if (input.matches('[data-addon-variant-option]')) {
          const root = input.closest('[data-product-addon]');
          const addon = this.addons.find((item) => item.root === root);
          if (addon && input.checked) {
            addon.selectFabricVariant(Number(input.value));
            // User picked a fabric type → gently reveal its colour choices.
            addon.scrollToChoices();
          }
          return;
        }

        if (input.matches('[data-addon-fabric-choice]')) {
          const root = input.closest('[data-product-addon]');
          const addon = this.addons.find((item) => item.root === root);
          if (addon && input.checked) addon.selectFabricChoice(input);
          return;
        }

        // Main product option change
        if (!input.name?.startsWith('option')) return;
        if (!this.addons.length) return;

        // Defer so Prestige can update selected variant JSON first
        requestAnimationFrame(() => {
          const target = this.getAsPicturedValue();
          const changedLabel = normalize(getOptionLabel(input));
          const sameOptionRadios = input.name
            ? Array.from(
                document.querySelectorAll(
                  `variant-picker input[type="radio"][name="${CSS.escape(input.name)}"]`
                )
              )
            : [];
          const isDesignModeGroup = sameOptionRadios.some((radio) => {
            const label = normalize(getOptionLabel(radio));
            return label === target || label.includes(target);
          });

          if (isDesignModeGroup && (changedLabel === target || changedLabel.includes(target))) {
            this.selectAsPictured({ selectMainVariant: false });
            return;
          }
          if (isDesignModeGroup) this.clearAsPictured();
          this.addons.forEach((addon) => addon.rematch());
        });
      });

      document.addEventListener('variant:change', (event) => {
        const changedVariant = event.detail?.variant || null;
        const mainPrice = Number(changedVariant?.price);
        if (Number.isFinite(mainPrice)) {
          document.querySelectorAll('[data-product-addons]').forEach((wrapper) => {
            wrapper.dataset.mainPrice = String(mainPrice);
          });
        }
        if (!this.addons.length) return;

        requestAnimationFrame(() => {
          this.addons.forEach((addon) => addon.rematch(changedVariant));
          this.updateDisplayedPrice();
        });
      });

      document.addEventListener('product:rerender', () => {
        this.refreshing = true;
        requestAnimationFrame(() => {
          this.refresh();
          this.refreshing = false;
        });
      }, true);
    }

    refresh() {
      this.addons = [];
      this.init();
      this.syncAsPicturedUi();
    }

    isMainVariantAsPicturedSelected() {
      const wrapper = document.querySelector('[data-product-addons]');
      const target = normalize(
        wrapper?.dataset.asPicturedValue || this.addons[0]?.asPicturedValue || AS_PICTURED_DEFAULT
      );
      if (!target) return false;

      const checked = Array.from(
        document.querySelectorAll('variant-picker input[type="radio"]:checked')
      );

      return checked.some((input) => {
        const label = normalize(getOptionLabel(input));
        // Exact-ish match on the option label text (not loose includes on huge strings)
        return label === target || label.includes(target);
      });
    }

    isAsPicturedSelected() {
      return this.asPicturedSelected;
    }

    selectAsPictured({ selectMainVariant = true } = {}) {
      this.asPicturedSelected = true;
      this.deselectFabricAddons();
      this.syncAsPicturedUi();
      if (selectMainVariant) this.selectMainOption(this.getAsPicturedValue());
      this.updateDisplayedPrice();
    }

    selectDesignMode() {
      this.asPicturedSelected = false;
      this.syncAsPicturedUi();
      const fabricAddon = this.addons.find((addon) => addon.mode === 'fabric');
      if (fabricAddon && !fabricAddon.selected) {
        fabricAddon.selectFabric();
      } else {
        fabricAddon?.openPanel();
      }
      this.updateModeSummary();
      this.selectDesignYourself();
    }

    clearAsPictured() {
      if (!this.asPicturedSelected) return;
      this.asPicturedSelected = false;
      this.syncAsPicturedUi();
    }

    syncAsPicturedUi() {
      document.querySelectorAll('[data-addon-as-pictured]').forEach((trigger) => {
        trigger.classList.toggle('is-selected', this.asPicturedSelected);
        trigger.setAttribute('aria-pressed', this.asPicturedSelected ? 'true' : 'false');
        trigger.closest('.product-addon')?.classList.toggle('is-selected', this.asPicturedSelected);
      });
      document.querySelectorAll('[data-addon-design-yourself]').forEach((trigger) => {
        trigger.classList.toggle('is-selected', !this.asPicturedSelected);
        trigger.setAttribute('aria-pressed', this.asPicturedSelected ? 'false' : 'true');
        trigger.closest('.product-addon')?.classList.toggle('is-selected', !this.asPicturedSelected);
      });
      document.querySelectorAll('[data-addon-design-options]').forEach((options) => {
        options.hidden = this.asPicturedSelected;
      });
      this.updateModeSummary();
    }

    updateModeSummary() {
      document.querySelectorAll('[data-product-addons]').forEach((wrapper) => {
        const summary = wrapper.querySelector('[data-addon-mode-label]');
        if (!summary) return;
        if (this.asPicturedSelected) {
          summary.textContent = wrapper.dataset.asPicturedLabel || 'As pictured';
          return;
        }

        const designLabel = wrapper.dataset.designLabel || 'Design Selv';
        const fabricAddon = this.addons.find((addon) => addon.mode === 'fabric' && addon.selected);
        const fabricLabel = fabricAddon?.getFabricDisplayLabel() || '';
        summary.textContent = [designLabel, fabricLabel].filter(Boolean).join(' · ');
      });
    }

    getAsPicturedValue() {
      const wrapper = document.querySelector('[data-product-addons]');
      return normalize(
        wrapper?.dataset.asPicturedValue || this.addons[0]?.asPicturedValue || AS_PICTURED_DEFAULT
      );
    }

    selectMainOption(target) {
      const radios = Array.from(document.querySelectorAll('variant-picker input[type="radio"]'));
      const option = radios.find((input) => {
        const label = normalize(getOptionLabel(input));
        return label === target || label.includes(target);
      });

      if (option && !option.checked) {
        // Avoid option.click(): the variant radio is visually hidden, so
        // clicking it moves focus to it and the browser scrolls it into view
        // (the page "jumps"). Dispatching a bubbling 'change' triggers the same
        // VariantPicker handler focus-free, keeping the scroll position.
        option.checked = true;
        option.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    selectDesignYourself() {
      this.selectMainOption(DESIGN_YOURSELF);
    }

    deselectAll({ silent = false } = {}) {
      this.addons.forEach((addon) => {
        if (addon.selected) addon.deselect({ silent: true });
      });
      selectionStore.clear();
      if (!silent) this.onAddonChange();
    }

    deselectFabricAddons() {
      this.addons.forEach((addon) => {
        if (addon.mode === 'fabric' && addon.selected) addon.deselect({ silent: true });
      });
    }

    onAddonChange() {
      const hasSelectedFabric = this.addons.some((addon) => addon.mode === 'fabric' && addon.selected);
      if (hasSelectedFabric && this.asPicturedSelected) {
        this.clearAsPictured();
      }
      if (hasSelectedFabric && this.isMainVariantAsPicturedSelected()) {
        this.selectDesignYourself();
      }
      this.updateModeSummary();
      this.updateDisplayedPrice();
    }

    getSelectedAddonsPrice() {
      return this.addons.reduce((total, addon) => {
        if (!addon.selected || !addon.selectedVariantId) return total;
        if (this.isAsPicturedSelected() && addon.mode === 'fabric') return total;
        return total + Number(addon.getSelectedVariant()?.price || 0);
      }, 0);
    }

    isMattressSelected() {
      return this.addons.some((addon) => {
        if (!addon.selected || !addon.selectedVariantId) return false;

        const label = addon.trigger
          ?.querySelector('.product-addon__label')
          ?.textContent?.trim();
        const identity = normalize([addon.type, addon.productTitle, label].filter(Boolean).join(' '));

        return /(^|[^a-z0-9])(mattress|madras)([^a-z0-9]|$)/.test(identity);
      });
    }

    updatePriceNote() {
      const mattressSelected = this.isMattressSelected();
      const wrapper = document.querySelector('[data-product-addons]');
      const productScope = wrapper?.closest('product-rerender') || document;

      productScope.querySelectorAll('[data-price-note]').forEach((note) => {
        const defaultText = note.dataset.priceNoteDefault || '';
        const includedText = note.dataset.priceNoteMattressIncluded || defaultText;
        note.textContent = mattressSelected ? includedText : defaultText;
      });
    }

    updateDisplayedPrice() {
      this.updatePriceNote();

      const wrapper = document.querySelector('[data-product-addons]');
      const mainPrice = Number(wrapper?.dataset.mainPrice);
      if (!wrapper || !Number.isFinite(mainPrice)) return;

      const totalPrice = mainPrice + this.getSelectedAddonsPrice();
      const productScope = wrapper.closest('product-rerender') || document;
      productScope
        .querySelectorAll('[data-block-type="price"] price-list.price-list--product sale-price')
        .forEach((price) => {
          const formattedPrice = formatMoney(totalPrice);
          let priceValue = price.querySelector('[data-product-price-value]');

          if (!priceValue) {
            Array.from(price.childNodes).forEach((node) => {
              if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) node.remove();
            });
            priceValue = document.createElement('span');
            priceValue.dataset.productPriceValue = '';
            price.append(priceValue);
          }

          priceValue.textContent = formattedPrice;
        });
    }

    getParentTitle() {
      return (
        document.querySelector('.product-title')?.textContent?.trim() ||
        document.querySelector('h1.product-title, h1')?.textContent?.trim() ||
        ''
      );
    }

    getSelectedItems() {
      const parentTitle = this.getParentTitle();

      return this.addons
        .filter((addon) => !(this.isAsPicturedSelected() && addon.mode === 'fabric'))
        .map((addon) => addon.getCartItem())
        .filter(Boolean)
        .map((item) => ({
          ...item,
          properties: {
            ...item.properties,
            ...(parentTitle ? { _parent_title: parentTitle } : {})
          }
        }));
    }
  }

  let controller;

  function ensureController() {
    if (!controller) controller = new ProductAddonsController(document);
    return controller;
  }

  function boot() {
    ensureController().init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.ProductAddons = {
    getSelectedItems(mainVariantId) {
      return ensureController().getSelectedItems(mainVariantId);
    },
    hasSelected() {
      const addonsController = ensureController();
      return addonsController.addons.some(
        (addon) =>
          addon.selected &&
          addon.selectedVariantId &&
          !(addonsController.isAsPicturedSelected() && addon.mode === 'fabric')
      );
    },
    deselectAll() {
      ensureController().deselectAll();
    },
    isAsPicturedSelected() {
      return ensureController().isAsPicturedSelected();
    },
    getController() {
      return ensureController();
    }
  };
})();